import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLabOrder } from '../labs-order.page';
import { usePatchLabOrderV1LabsOrdersOrderIdPatch } from '../../../../api/generated-catch-backend/laboratory-orders/laboratory-orders';
import { useSearchPatientServiceCentersV1LabsPatientServiceCentersGet } from '../../../../api/generated-catch-backend/patient-service-centers/patient-service-centers';
import type { PatientServiceCenterDTO } from '../../../../api/generated-catch-backend/model';
import { ROUTE_LABS_ORDER_STEP_2, ROUTE_LABS_ORDER_STEP_4 } from '../../../../constants/routes';
import { LabsOrderStepLayout, LocationInput, FormSelect } from '../components';

export function Step3SelectLab() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { activeOrder, isLoadingOrder, refetchOrder } = useLabOrder();

  const [selectedLabSiteCode, setSelectedLabSiteCode] = useState<string | null>(null);
  const [zipCode, setZipCode] = useState<string>('');
  const [radius, setRadius] = useState<10 | 20 | 25 | 50 | 100>(50);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const patchOrderMutation = usePatchLabOrderV1LabsOrdersOrderIdPatch();

  // Initialize zip code from active order only
  useEffect(() => {
    if (zipCode) return; // Already initialized

    // Get zip code from active order
    if (activeOrder?.postalCode) {
      setZipCode(activeOrder.postalCode);
    }
  }, [activeOrder, zipCode]);

  // Prefill selected lab if order already has one
  useEffect(() => {
    if (activeOrder?.junctionSiteCode && !selectedLabSiteCode) {
      setSelectedLabSiteCode(activeOrder.junctionSiteCode);
    }
  }, [activeOrder, selectedLabSiteCode]);

  // Fetch labs based on current zip code
  const { data: labsData, isLoading, error } = useSearchPatientServiceCentersV1LabsPatientServiceCentersGet(
    {
      'zip-code': zipCode,
      radius: radius,
    },
    {
      query: {
        enabled: !!zipCode && zipCode.length === 5, // Only fetch when we have a valid 5-digit zip
      },
    }
  );

  // Sort labs by distance, nearest first
  const labs = useMemo(() => {
    const labsList = labsData?.data || [];
    return [...labsList].sort((a, b) => a.distance - b.distance);
  }, [labsData]);

  const selectedLab = useMemo(() => {
    return labs.find(lab => lab.siteCode === selectedLabSiteCode) || null;
  }, [labs, selectedLabSiteCode]);

  const handleSelectLab = (siteCode: string) => {
    setSelectedLabSiteCode(siteCode);
    setErrorMessage(null);
  };

  const handleUseCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setErrorMessage('Geolocation is not supported by your browser');
      return;
    }

    setIsGettingLocation(true);
    setErrorMessage(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          console.log('Got position:', position.coords.latitude, position.coords.longitude);

          // Use BigDataCloud's free reverse geocoding API (no API key required)
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}&localityLanguage=en`
          );

          if (!response.ok) {
            console.error('Reverse geocoding API error:', response.status, response.statusText);
            setErrorMessage('Failed to get location. Please enter your zip code manually.');
            setIsGettingLocation(false);
            return;
          }

          const data = await response.json();
          console.log('Reverse geocoding API response:', data);

          if (data.postcode) {
            console.log('Found zip code:', data.postcode);
            setZipCode(data.postcode);
          } else {
            console.error('No postcode found in response');
            setErrorMessage('Could not determine zip code from your location');
          }
        } catch (error) {
          console.error('Error getting location:', error);
          setErrorMessage('Failed to get your location. Please try again.');
        } finally {
          setIsGettingLocation(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setErrorMessage('Failed to get your location. Please ensure location permissions are enabled.');
        setIsGettingLocation(false);
      }
    );
  }, []);

  const handleContinue = async () => {
    if (!selectedLab) {
      setErrorMessage('Please select a lab to continue');
      return;
    }

    if (!activeOrder || !activeOrder.orderId) {
      setErrorMessage('No active order found. Please go back and complete the previous step.');
      return;
    }

    setErrorMessage(null);

    // Validate lab provider
    if (!selectedLab.labProvider) {
      setErrorMessage('Invalid lab provider. Please select a different lab.');
      return;
    }

    try {
      // Use PATCH to select the patient service center
      await patchOrderMutation.mutateAsync({
        orderId: activeOrder.orderId,
        data: {
          operation: 'select_psc',
          data: {
            labProvider: selectedLab.labProvider,
            junctionSiteCode: selectedLab.siteCode,
            pscZipCode: selectedLab.metadata.zip,
          },
        },
      });

      // Refetch order to get updated state
      await refetchOrder();

      // Navigate to step 4, passing the selected lab data in state
      const source = searchParams.get('source');
      const url = source ? `${ROUTE_LABS_ORDER_STEP_4}?source=${source}` : ROUTE_LABS_ORDER_STEP_4;
      navigate(url, {
        state: { selectedLab },
      });
    } catch (error) {
      console.error('Error selecting lab:', error);
      setErrorMessage('Failed to select lab. Please try again.');
    }
  };

  const handleBack = () => {
    const source = searchParams.get('source');
    const url = source ? `${ROUTE_LABS_ORDER_STEP_2}?source=${source}` : ROUTE_LABS_ORDER_STEP_2;
    navigate(url);
  };

  const handleZipCodeChange = (value: string) => {
    const cleanedValue = value.replace(/\D/g, '').slice(0, 5);
    setZipCode(cleanedValue);
  };

  const openInMaps = (lab: PatientServiceCenterDTO) => {
    const address = encodeURIComponent(
      `${lab.metadata.address}, ${lab.metadata.city}, ${lab.metadata.state} ${lab.metadata.zip}`
    );
    window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
  };

  // Show loading state while checking for order
  if (isLoadingOrder) {
    return (
      <LabsOrderStepLayout currentStep={3} onBack={handleBack}>
        <div className="text-center py-8">
          <p className="text-[#9E9E9E]">Loading...</p>
        </div>
      </LabsOrderStepLayout>
    );
  }

  // Allow user to provide zip code even if none is saved
  return (
    <LabsOrderStepLayout currentStep={3} onBack={handleBack}>
      {/* Intro Text */}
      <p className="mb-6 text-[14px] leading-[170%] text-[#3B3B3B] opacity-80">
        We're showing the closest lab locations based on your zip code. Select the one that works best for you to continue.
      </p>

      {/* Error Message */}
      {errorMessage && (
        <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-[14px] text-red-800">
          {errorMessage}
        </div>
      )}

      {/* Search Controls */}
      <div className="mb-6 space-y-4">
        {/* Zip Code with Location */}
        <LocationInput
          id="zipCode"
          label="Zip Code"
          value={zipCode}
          onChange={handleZipCodeChange}
          maxLength={5}
          onUseCurrentLocation={handleUseCurrentLocation}
          isGettingLocation={isGettingLocation}
        />

        {/* Search Radius */}
        <FormSelect
          id="radius"
          label="Select Search Radius"
          value={String(radius)}
          onChange={(value) => setRadius(Number(value) as typeof radius)}
          options={[
            { value: '10', label: '10 miles' },
            { value: '20', label: '20 miles' },
            { value: '25', label: '25 miles' },
            { value: '50', label: '50 miles' },
            { value: '100', label: '100 miles' },
          ]}
        />
      </div>

      {/* Lab List */}
      <div className="space-y-3 mb-6">
        {!zipCode || zipCode.length < 5 ? (
          <div className="py-8 text-center text-[#9E9E9E]">
            Please enter a 5-digit zip code to see available labs.
          </div>
        ) : isLoading ? (
          <div className="py-8 text-center text-[#9E9E9E]">Loading labs...</div>
        ) : error ? (
          <div className="py-8 text-center text-red-600">Error loading labs. Please try again.</div>
        ) : labs.length === 0 ? (
          <div className="py-8 text-center text-[#3B3B3B]">
            There were no labs found in your area. Please try expanding your search radius to see more options.
          </div>
        ) : (
          labs.map((lab) => (
            <div
              key={lab.siteCode}
              onClick={() => handleSelectLab(lab.siteCode)}
              style={{
                borderWidth: selectedLabSiteCode === lab.siteCode ? '2px' : '1px',
                borderStyle: 'solid',
                borderColor: selectedLabSiteCode === lab.siteCode ? '#0C6A58' : '#E5E5E5',
              }}
              className={`relative rounded-lg p-4 cursor-pointer transition-all ${
                selectedLabSiteCode === lab.siteCode
                  ? 'shadow-sm'
                  : 'hover:border-[#BDBDBD] hover:shadow-sm'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 pr-4">
                  <h3 className="text-[18px] font-bold leading-[24px] text-[#183344] mb-2">
                    {lab.metadata.labName || `Lab ${lab.siteCode}`}
                  </h3>
                  <p className="text-[14px] font-normal leading-[140%] text-[#63605D]">
                    {lab.metadata.address}
                    <br />
                    {lab.metadata.city}, {lab.metadata.state} {lab.metadata.zip}
                  </p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openInMaps(lab);
                    }}
                    className="mt-2 inline-flex items-center gap-1 text-[14px] font-bold leading-[140%] text-[#183344] hover:text-[#0D1F2D] transition-colors underline"
                  >
                    See Location
                    <svg
                      width="13"
                      height="12"
                      viewBox="0 0 13 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="inline-block"
                    >
                      <path
                        d="M11.1668 1.33301L2.16683 10.333M11.1668 1.33301H3.66683M11.1668 1.33301V8.83301"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
                <div className="flex-shrink-0">
                  {selectedLabSiteCode === lab.siteCode ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="20" viewBox="0 0 19 20" fill="none">
                      <circle cx="9.5" cy="10" r="9" fill="#0C6A58"/>
                      <circle cx="9.5" cy="10" r="9" stroke="#0C6A58"/>
                      <circle cx="9.5" cy="10" r="9" stroke="black" strokeOpacity="0.2"/>
                    </svg>
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-[#BDBDBD]"></div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Spacer to prevent content from being hidden behind sticky button */}
      <div className="h-20" />

      {/* Action Buttons - Sticky */}
      {selectedLab && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-10">
          <div className="max-w-2xl mx-auto">
            <button
              type="button"
              onClick={handleContinue}
              disabled={patchOrderMutation.isPending}
              className="w-full rounded-full bg-[#7CF1C7] px-8 py-3.5 text-center text-[17px] font-bold leading-[26px] text-[#183344] transition-all hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {patchOrderMutation.isPending ? 'Saving...' : 'Continue'}
            </button>
          </div>
        </div>
      )}
    </LabsOrderStepLayout>
  );
}
